import { FieldPath, FieldValues, get, useController } from 'react-hook-form';
import { Timestamp, TimestampType } from '@britishcouncil/common-core';
import {
  DatePickerRangeValue,
  FormDatePicker,
  FormDatePickerProps,
} from '@britishcouncil/react-admin';

import { formatDate } from 'utils';
import { RHFormFieldProps } from './types';
import { useFormContext } from './FormContext';
import { ReadonlyFormValue } from './ReadonlyFormValue';

type ISODateRangeValue = [string | null, string | null] | null;
type DatePickerValue = string | ISODateRangeValue | null;

type RHFormDatePickerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = RHFormFieldProps<FormDatePickerProps<DatePickerValue>, TFieldValues, TName> & {
  readonlyMode?: boolean;
  onChange?: (value: DatePickerValue) => void;
};

export const RHFormDatePicker = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  shouldUnregister,
  defaultValue,
  readonlyMode,
  onChange,
  placeholder,
  ...props
}: RHFormDatePickerProps<TFieldValues, TName>) => {
  const { mode } = useFormContext();
  const { field, formState } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const handleOnChange = (value: TimestampType | DatePickerRangeValue | null) => {
    const isoValue = convertToISOString(value);
    field.onChange(isoValue);
    onChange?.(isoValue);
  };

  if (mode === 'read' || readonlyMode) {
    return (
      <ReadonlyFormValue
        label={props.label}
        value={field.value}
        renderValue={(value) => (value ? formatDate(value) : (placeholder ?? '-'))}
      />
    );
  }

  return (
    <FormDatePicker
      name={name}
      {...props}
      placeholder={placeholder ?? 'Select date'}
      format="dd/MM/yyyy"
      value={convertToTimestamp(field.value)}
      onChange={handleOnChange}
      onBlur={field.onBlur}
      touched
      error={get(formState.errors, name)?.message}
      fullWidth
    />
  );
};

const convertToTimestamp = (
  value: DatePickerValue
): TimestampType | DatePickerRangeValue | null => {
  if (!value) return null;

  // Range mode
  if (Array.isArray(value)) {
    return value.map((v) => (v ? Timestamp.parse(v) : undefined)) as DatePickerRangeValue;
  }

  // Single mode
  return Timestamp.parse(value);
};

const convertToISOString = (
  value: TimestampType | DatePickerRangeValue | null
): DatePickerValue => {
  if (!value) return null;

  // Range mode
  if (Array.isArray(value)) {
    return value.map((v) => (v ? Timestamp.toISOString(v) : null)) as ISODateRangeValue;
  }

  // Single mode
  return Timestamp.toISOString(value);
};
